import React, { useState, useEffect } from 'react';
import { useWeb3 } from '../../contexts/Web3Context';
import { 
  ClockIcon, 
  CurrencyDollarIcon,
  UserIcon,
  FireIcon,
  CheckCircleIcon 
} from '@heroicons/react/24/outline';

interface AuctionData {
  id: string;
  nftId: string;
  tokenId: string;
  title: string;
  description: string;
  image: string;
  seller: string;
  sellerName: string;
  startingPrice: number;
  currentBid: number;
  currency: string;
  startTime: number;
  endTime: number;
  bidCount: number;
  status: string;
  highestBidder: string;
  highestBidderName: string;
  bids?: Array<{
    bidder: string;
    bidderName: string;
    amount: number;
    timestamp: number;
    avatar: string;
  }>;
}

const NFTAuction: React.FC = () => {
  const { wallet } = useWeb3();
  const [auctions, setAuctions] = useState<AuctionData[]>([]);
  const [selectedAuction, setSelectedAuction] = useState<AuctionData | null>(null);
  const [bidAmount, setBidAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [bidding, setBidding] = useState(false);

  useEffect(() => {
    loadAuctions();
  }, []);

  const loadAuctions = async () => {
    setLoading(true);
    try {
      // 使用新的mock数据结构
      const { UnifiedMockData } = await import('../../data/mock');
      const mockData = new UnifiedMockData();
      const auctionData = mockData.getNFTAuctions();
      setAuctions(auctionData);
    } catch (error) {
      console.error('Failed to load auctions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceBid = async (auctionId: string) => {
    if (!wallet?.address || !bidAmount) return;
    
    setBidding(true);
    try {
      // Mock bid placement
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Update auction with new bid
      setAuctions(prev => prev.map(auction => {
        if (auction.id === auctionId) {
          const newBid = {
            bidder: wallet.address,
            bidderName: 'You',
            amount: parseFloat(bidAmount),
            timestamp: Date.now(),
            avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face'
          };
          
          return {
            ...auction,
            currentBid: parseFloat(bidAmount),
            bidCount: auction.bidCount + 1,
            highestBidder: wallet.address,
            highestBidderName: 'You',
            bids: [newBid, ...(auction.bids || [])]
          };
        }
        return auction;
      }));
      
      setBidAmount('');
      alert('出价成功！');
    } catch (error) {
      console.error('Failed to place bid:', error);
      alert('出价失败，请重试');
    } finally {
      setBidding(false);
    }
  };

  const formatTimeRemaining = (endTime: number) => {
    const now = Date.now();
    const remaining = endTime - now;
    
    if (remaining <= 0) return '已结束';
    
    const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}天 ${hours}小时`;
    if (hours > 0) return `${hours}小时 ${minutes}分钟`;
    return `${minutes}分钟`;
  };

  const AuctionCard: React.FC<{ auction: AuctionData }> = ({ auction }) => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
      <div className="relative">
        <img 
          src={auction.image} 
          alt={auction.title}
          className="w-full h-64 object-cover"
        />
        <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-medium">
          <ClockIcon className="w-4 h-4 inline mr-1" />
          {formatTimeRemaining(auction.endTime)}
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{auction.title}</h3>
        <p className="text-gray-600 mb-4 line-clamp-2">{auction.description}</p>
        
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-gray-500">当前出价</p>
            <p className="text-2xl font-bold text-green-600">
              {auction.currentBid} {auction.currency}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">出价次数</p>
            <p className="text-lg font-semibold">{auction.bidCount}</p>
          </div>
        </div>
        
        <div className="flex items-center mb-4">
          <UserIcon className="w-5 h-5 text-gray-400 mr-2" />
          <span className="text-sm text-gray-600">
            最高出价者: {auction.highestBidderName}
          </span>
        </div>
        
        <button
          onClick={() => setSelectedAuction(auction)}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
        >
          查看详情 & 出价
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">NFT 拍卖市场</h2>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <FireIcon className="w-5 h-5 text-red-500" />
          <span>{auctions.length} 个活跃拍卖</span>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {auctions.map(auction => (
          <AuctionCard key={auction.id} auction={auction} />
        ))}
      </div>
      
      {/* 拍卖详情模态框 */}
      {selectedAuction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">{selectedAuction.title}</h3>
                <button
                  onClick={() => setSelectedAuction(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <img 
                  src={selectedAuction.image} 
                  alt={selectedAuction.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500">当前出价</p>
                    <p className="text-3xl font-bold text-green-600">
                      {selectedAuction.currentBid} {selectedAuction.currency}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">剩余时间</p>
                    <p className="text-lg font-semibold text-red-600">
                      {formatTimeRemaining(selectedAuction.endTime)}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">出价次数</p>
                    <p className="text-lg font-semibold">{selectedAuction.bidCount}</p>
                  </div>
                </div>
              </div>
              
              {wallet?.address && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-3">出价</h4>
                  <div className="flex space-x-3">
                    <input
                      type="number"
                      value={bidAmount}
                      onChange={(e) => setBidAmount(e.target.value)}
                      placeholder={`最低出价: ${selectedAuction.currentBid + 0.1} ${selectedAuction.currency}`}
                      min={selectedAuction.currentBid + 0.1}
                      step="0.1"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      onClick={() => handlePlaceBid(selectedAuction.id)}
                      disabled={bidding || !bidAmount || parseFloat(bidAmount) <= selectedAuction.currentBid}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {bidding ? '出价中...' : '出价'}
                    </button>
                  </div>
                </div>
              )}
              
              {/* 出价历史 */}
              {selectedAuction.bids && selectedAuction.bids.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">出价历史</h4>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {selectedAuction.bids.map((bid, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <img 
                            src={bid.avatar} 
                            alt={bid.bidderName}
                            className="w-8 h-8 rounded-full"
                          />
                          <div>
                            <p className="font-medium">{bid.bidderName}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(bid.timestamp).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-600">
                            {bid.amount} {selectedAuction.currency}
                          </p>
                          {index === 0 && (
                            <CheckCircleIcon className="w-4 h-4 text-green-500 inline ml-1" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NFTAuction;
